import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { DASHBOARD_CALCULATIONS } from "./DashboardCalculationHover.jsx";
import { buildDashboardKpiDetails } from "../utils/dashboardKpiDetails.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { focusBillingDialogElement, handleBillingDialogKeyDown } from "../utils/billingDialogAccessibility.js";
import "./FleetUtilizationCard.css";

export default function DashboardKpiDialog({ cell, period, summary, previousSummary, onClose }) {
  const dialogRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const titleId = React.useId();
  const details = buildDashboardKpiDetails(cell.key, period.days || [], summary);
  const rows = [["Période sélectionnée", cell.text(summary)],
    ["Période précédente", previousSummary ? cell.text(previousSummary) : "indisponible"],
    ...(cell.details ? cell.details(summary) : [])];
  React.useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    focusBillingDialogElement(closeRef.current);
    return () => {
      document.body.style.overflow = previousOverflow;
      focusBillingDialogElement(previousFocus);
    };
  }, []);

  return createPortal(<div className="fleet-dialog-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} className="fleet-dialog dashboard-kpi-dialog rounded-[4px]"
      onKeyDown={(event) => handleBillingDialogKeyDown(event, { activeElement: document.activeElement, dialog: dialogRef.current, onClose })}>
      <header className="fleet-card-header">
        <div><h2 id={titleId}>Détail : {cell.label}</h2><p>{period.label} · Données planifiées</p></div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Fermer le détail de l’indicateur"
          className="rounded-[4px] border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-accent"><X size={18} /></button>
      </header>
      <p className="fleet-rule">{DASHBOARD_CALCULATIONS[cell.label]}</p>
      <dl className="fleet-day-metrics">{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
      {details.formula ? <div className="dashboard-low-load-formula rounded-[4px]" aria-label="Formule du calcul"><strong>{details.formula}</strong></div>
        : <p className="fleet-rule" role="status">Données insuffisantes pour calculer cet indicateur sur la sélection.</p>}
      <p className="fleet-rule">{details.note}</p>
      <div className="fleet-table-scroll">
        <table className="fleet-trucks-table dashboard-kpi-detail-table">
          <caption className="sr-only">{cell.label} par dispatch</caption>
          <thead><tr><th scope="col">Dispatch</th>{details.columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr></thead>
          <tbody>{details.rows.map((row, index) => <tr key={index}>
            <th scope="row">{row.date}{!row.valid && <span className="fleet-row-note">Hors calcul · données insuffisantes</span>}{row.note && <span className="fleet-row-note">{row.note}</span>}</th>
            {row.values.map((value, column) => <td key={column}>{value}</td>)}
          </tr>)}</tbody>
          <tfoot><tr><th scope="row">{details.totalLabel}</th>{details.totals.map((value, column) => <td key={column}>{value}</td>)}</tr></tfoot>
        </table>
      </div>
      <p className="fleet-muted">{details.validCount} / {details.totalCount} dispatchs calculables. Les valeurs affichées sont arrondies ; le calcul utilise les valeurs avant arrondi.</p>
    </section>
  </div>, getReactPortalRoot());
}
